// Mini calendario mensual (mock `.cal-card`, tema success): navegación de mes + selección de día,
// con rango pintado entre desde/hasta. Semana inicia en lunes.
import { cn } from '@/lib/cn';

function pad2(n: number): string {
    return String(n).padStart(2, '0');
}

/** ISO local (sin sorpresas de timezone). */
export function isoOf(date: Date): string {
    return `${String(date.getFullYear())}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function firstOfMonth(iso: string): Date {
    const d = new Date(`${iso}T00:00:00`);
    return new Date(d.getFullYear(), d.getMonth(), 1);
}

const DAY_LABEL = new Intl.DateTimeFormat('es-MX', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
});

export function MiniCalendar({
    month,
    from,
    to,
    today,
    disabled = false,
    onNavigate,
    onPick,
}: {
    /** Primer día del mes mostrado. */
    month: Date;
    from: string | null;
    to: string | null;
    today: string;
    disabled?: boolean;
    onNavigate: (dir: -1 | 1) => void;
    onPick: (iso: string) => void;
}) {
    const y = month.getFullYear();
    const m = month.getMonth();
    // Capitaliza SOLO la inicial: el `capitalize` de CSS también sube la preposición y pinta
    // «Agosto De 2026».
    const rawMonth = month.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
    const monthName = rawMonth.charAt(0).toUpperCase() + rawMonth.slice(1);
    let firstDayOfWeek = new Date(y, m, 1).getDay();
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    const daysInMonth = new Date(y, m + 1, 0).getDate();

    return (
        <div
            className={cn(
                'rounded-md border-[1.5px] p-1',
                disabled
                    ? 'border-border bg-card opacity-55 grayscale'
                    : 'border-success-soft-border bg-success-soft',
            )}
        >
            <div className="mb-0.5 flex items-center justify-between gap-0.5">
                <button
                    type="button"
                    aria-label="Mes anterior"
                    disabled={disabled}
                    onClick={() => {
                        onNavigate(-1);
                    }}
                    className={cn(
                        'flex size-4 items-center justify-center rounded text-xs',
                        disabled ? 'text-muted-foreground' : 'text-success hover:bg-success/15',
                    )}
                >
                    ‹
                </button>
                <div
                    className={cn(
                        'flex-1 text-center text-[10px] font-bold',
                        disabled ? 'text-muted-foreground' : 'text-success',
                    )}
                >
                    {monthName}
                </div>
                <button
                    type="button"
                    aria-label="Mes siguiente"
                    disabled={disabled}
                    onClick={() => {
                        onNavigate(1);
                    }}
                    className={cn(
                        'flex size-4 items-center justify-center rounded text-xs',
                        disabled ? 'text-muted-foreground' : 'text-success hover:bg-success/15',
                    )}
                >
                    ›
                </button>
            </div>
            <div
                className={cn(
                    'grid grid-cols-7 pb-0.5 text-center text-[8.5px]',
                    disabled ? 'text-muted-foreground' : 'text-success/80',
                )}
            >
                {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, i) => (
                    <span key={`${d}-${String(i)}`}>{d}</span>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-px">
                {Array.from({ length: firstDayOfWeek }, (_, i) => (
                    <div key={`pad-${String(i)}`} className="h-[15px]" />
                ))}
                {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const iso = `${String(y)}-${pad2(m + 1)}-${pad2(day)}`;
                    const selected = iso === from || (!!to && iso === to);
                    const inRange = !!from && !!to && iso > from && iso < to;
                    return (
                        <button
                            key={iso}
                            type="button"
                            disabled={disabled}
                            aria-pressed={selected}
                            aria-label={DAY_LABEL.format(new Date(y, m, day))}
                            onClick={() => {
                                onPick(iso);
                            }}
                            className={cn(
                                'flex h-[15px] items-center justify-center text-[9px]',
                                selected
                                    ? 'rounded-[3px] bg-success font-bold text-card'
                                    : inRange
                                      ? 'bg-success/20'
                                      : 'rounded-[3px] hover:bg-success/15',
                                iso === today && !selected && 'rounded-[3px] border border-info',
                            )}
                        >
                            {day}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
